import { createElement } from 'react';
import {
  AllyIcon,
  PlainLanguageIcon,
  TranscriptionIcon,
} from '@/components/customIcons';
import { access } from '@/access';
import type { MenuItem } from '@/layout/Sidebar/MenuList/types';
import {
  IconAccessible,
  IconAdjustmentsHorizontal,
  IconBrowserCheck,
  IconBuildingLighthouse,
  IconChartLine,
  IconChecklist,
  IconCheckupList,
  IconClipboardPlus,
  IconEye,
  IconEyeCheck,
  IconFileCheck,
  IconFileDescription,
  IconFilePencil,
  IconFileStar,
  IconFileInfo,
  IconFileTypePdf,
  IconFileUpload,
  IconFlask,
  IconHeartHandshake,
  IconHome2,
  IconKey,
  IconLanguage,
  IconListDetails,
  IconLockSquare,
  IconPalette,
  IconRadar,
  IconReport,
  IconReportAnalytics,
  IconSettings,
  IconTargetArrow,
  IconTextSpellcheck,
  IconTools,
  IconTypography,
  IconUnlink,
  IconUsersGroup,
  IconUserShield,
  IconWorldStar,
  IconZoomExclamation,
} from '@tabler/icons-react';
import { Sparkles, type LucideProps } from 'lucide-react';

const AiNavIcon = (props: LucideProps) =>
  createElement(Sparkles, {
    ...props,
    strokeWidth: 2,
    className: `${props.className ?? ''} eye:p-[2px]`,
  });

/**
 * The sidebar's visible navigation hierarchy.
 *
 * Route definitions stay in routes.tsx; this file deliberately contains only
 * presentation and visibility data needed to render the sidebar. Detail routes
 * that are reached from a visible item are not navigation entries.
 */
const navigation: MenuItem[] = [
  {
    path: '/',
    handle: {
      i18nNameKey: 'homepage',
      icon: IconHome2,
      access: access.user,
      type: 'item',
    },
  },
  {
    path: '/organisation',
    handle: {
      type: 'item',
      i18nNameKey: 'myOrg',
      icon: IconUsersGroup,
      access: access.organisation,
      i18nTooltipKey: 'myOrgTooltip',
    },
  },
  {
    path: '/partner',
    handle: {
      type: 'collapse',
      name: 'Partnerarea',
      icon: IconHeartHandshake,
      access: access.partner,
      i18nTooltipKey: 'myPartnerAreaTooltip',
      i18nNameKey: 'myPartnerArea',
      badge: { text: 'new', showWhen: 'always' },
    },
    children: [
      {
        path: '/partner/marketing',
        handle: {
          i18nNameKey: 'marketing',
          access: access.partner,
          type: 'item',
          icon: IconWorldStar,
        },
      },
      {
        path: '/partner/licenses',
        handle: {
          i18nNameKey: 'licenseOverview',
          access: access.partner,
          type: 'item',
          icon: IconEye,
        },
      },
    ],
  },
  {
    path: '/assist',
    handle: {
      name: 'Eye-Able Assist',
      icon: IconAccessible,
      access: access.user,
      type: 'collapse',
      i18nTooltipKey: 'EaAssistTooltip',
      showDividerBeforeThis: true,
      i18nDividerKey: 'assistSoftware',
      badge: { text: 'demo', showWhen: 'assistDemoMode' },
    },
    children: [
      {
        path: '/assist/overview',
        handle: {
          i18nNameKey: 'overview',
          access: access.user,
          type: 'item',
          icon: IconEye,
        },
      },
      {
        path: '/assist/customize',
        handle: {
          i18nNameKey: 'configurator',
          access: access.user,
          type: 'item',
          icon: IconAdjustmentsHorizontal,
        },
      },
      {
        path: '/assist/installation',
        handle: {
          i18nNameKey: 'installation',
          access: access.user,
          type: 'item',
          icon: IconTools,
        },
      },
      {
        path: '/assist/data-privacy',
        handle: {
          i18nNameKey: 'dataPrivacy',
          access: access.user,
          type: 'item',
          icon: IconLockSquare,
        },
      },
    ],
  },
  {
    path: '/plain-language',
    handle: {
      i18nNameKey: 'plainLanguage',
      icon: PlainLanguageIcon,
      access: access.user,
      type: 'collapse',
    },
    children: [
      {
        path: '/plain-language/simplify',
        handle: {
          i18nNameKey: 'simplifyText',
          access: access.user,
          type: 'item',
          icon: IconTypography,
        },
      },
      {
        path: '/plain-language/document',
        handle: {
          i18nNameKey: 'simplifyDocument',
          access: access.user,
          type: 'item',
          icon: IconFileUpload,
        },
      },
      {
        path: '/plain-language/websites',
        handle: {
          i18nNameKey: 'websites',
          access: access.plainLanguage,
          icon: IconListDetails,
          badge: { text: 'demo', showWhen: 'easyDemoMode' },
        },
      },
      {
        path: '/plain-language/configurator',
        handle: {
          i18nNameKey: 'configurator',
          access: access.plainLanguage,
          type: 'item',
          icon: IconAdjustmentsHorizontal,
        },
      },
      {
        path: '/plain-language/installation',
        handle: {
          i18nNameKey: 'installation',
          access: access.plainLanguage,
          type: 'item',
          icon: IconTools,
          badge: { text: 'demo', showWhen: 'easyDemoMode' },
        },
      },
      {
        path: '/plain-language/dataprivacy',
        handle: {
          i18nNameKey: 'dataPrivacy',
          access: access.user,
          type: 'item',
          icon: IconLockSquare,
        },
      },
    ],
  },
  {
    path: '/translate',
    handle: {
      name: 'Translate',
      icon: IconLanguage,
      access: access.translate,
      type: 'collapse',
      badge: { text: 'demo', showWhen: 'translateDemoMode' },
    },
    children: [
      {
        path: '/translate/websites',
        handle: {
          i18nNameKey: 'websites',
          access: access.user,
          type: 'item',
          icon: IconListDetails,
          activeOnPaths: ['/translate/websites/*'],
        },
      },
      {
        path: '/translate/configurator',
        handle: {
          i18nNameKey: 'configurator',
          access: access.user,
          type: 'item',
          icon: IconAdjustmentsHorizontal,
        },
      },
      {
        path: '/translate/installation',
        handle: {
          i18nNameKey: 'installation',
          access: access.user,
          type: 'item',
          icon: IconTools,
        },
      },
      {
        path: '/translate/dataprivacy',
        handle: {
          i18nNameKey: 'dataPrivacy',
          access: access.user,
          type: 'item',
          icon: IconLockSquare,
        },
      },
    ],
  },
  {
    path: '/audit',
    handle: {
      name: 'Eye-Able Audit',
      icon: IconRadar,
      access: access.user,
      type: 'item',
      badge: { text: 'demo', showWhen: 'auditDemoMode' },
      i18nTooltipKey: 'EaAuditTooltip',
      showDividerBeforeThis: true,
      i18nDividerKey: 'auditSoftware',
    },
  },
  {
    path: '/report',
    handle: {
      name: 'Eye-Able Report',
      icon: IconReport,
      access: access.user,
      type: 'collapse',
      i18nTooltipKey: 'EaReportTooltip',
      badge: { text: 'demo', showWhen: 'reportDemoMode' },
    },
    children: [
      {
        path: '/report/overview',
        handle: {
          i18nNameKey: 'overview',
          icon: IconEyeCheck,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/report/issues',
        handle: {
          i18nNameKey: 'issues',
          icon: IconZoomExclamation,
          access: access.user,
          type: 'item',
          activeOnPaths: ['/report/issues/issue/*'],
        },
      },
      {
        path: '/report/pages',
        handle: {
          i18nNameKey: 'mySites',
          icon: IconBrowserCheck,
          access: access.user,
          type: 'item',
          activeOnPaths: ['/report/pages/*'],
        },
      },
      {
        path: '/report/settings',
        handle: {
          i18nNameKey: 'settings',
          icon: IconSettings,
          access: access.user,
          type: 'item',
        },
      },
    ],
  },
  {
    path: '/report/my-goals',
    handle: {
      i18nNameKey: 'myGoals',
      icon: IconTargetArrow,
      access: access.user,
      type: 'item',
      badge: { text: 'demo', showWhen: 'reportDemoMode' },
    },
  },
  {
    path: '/report/tests',
    handle: {
      i18nNameKey: 'guidedTesting',
      icon: IconClipboardPlus,
      access: access.user,
      type: 'item',
      activeOnPaths: ['/report/tests/*'],
    },
  },
  {
    path: '/report/pdfs',
    handle: {
      name: 'PDFs',
      icon: IconFileTypePdf,
      access: access.user,
      type: 'collapse',
      badge: { text: 'demo', showWhen: 'reportDemoMode' },
    },
    children: [
      {
        path: '/report/pdfs/',
        handle: {
          i18nNameKey: 'foundPdfs',
          access: access.user,
          type: 'item',
          icon: IconFileCheck,
          activeOnPaths: ['/report/pdfs/found/pdf/*'],
        },
      },
      {
        path: '/report/pdfs/upload',
        handle: {
          i18nNameKey: 'userPdfs',
          icon: IconFileUpload,
          access: access.user,
          type: 'item',
          activeOnPaths: ['/report/pdfs/upload/pdf/*'],
        },
      },
      {
        path: '/report/pdfs/pdfsIssues',
        handle: {
          i18nNameKey: 'issues',
          access: access.user,
          type: 'item',
          icon: IconZoomExclamation,
        },
      },
      {
        path: '/report/pdfs/improvePDF',
        handle: {
          i18nNameKey: 'improvements',
          icon: IconFileUpload,
          access: access.user,
          type: 'item',
          activeOnPaths: [
            '/report/pdfs/improvePDF/upload/pdf/*',
            '/report/pdfs/improvePDF/found/pdf/*',
          ],
        },
      },
    ],
  },
  {
    path: '/report/analysis',
    handle: {
      name: 'Content & Web Analysis',
      icon: IconChartLine,
      access: access.user,
      type: 'collapse',
      badge: { text: 'demo', showWhen: 'reportDemoMode' },
    },
    children: [
      {
        path: '/report/analysis/broken-links',
        handle: {
          name: 'Broken Links',
          icon: IconUnlink,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/report/analysis/lighthouse',
        handle: {
          name: 'Lighthouse',
          icon: IconBuildingLighthouse,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/report/analysis/spellchecker',
        handle: {
          i18nNameKey: 'spellChecking',
          icon: IconTextSpellcheck,
          access: access.user,
          type: 'item',
        },
      },
      ...((window.location.origin.includes('localhost') ||
      window.location.origin.includes('dev-3') ||
      window.location.origin.includes('dev.dashboard')
        ? [
            {
              path: '/report/analysis/fontOverview/',
              handle: {
                name: 'fontOverview',
                access: access.user,
                i18nNameKey: 'fontOverview',
                icon: IconFileInfo,
                type: 'item',
                badge: { text: 'new', showWhen: 'always' },
              },
            },
          ]
        : []) satisfies MenuItem[]),
    ],
  },
  {
    path: '/governance',
    handle: {
      i18nNameKey: 'governance',
      icon: IconCheckupList,
      access: access.user,
      type: 'collapse',
      badge: { text: 'beta', showWhen: 'always' },
    },
    children: [
      {
        path: '/governance/overview',
        handle: {
          i18nNameKey: 'overview',
          icon: IconListDetails,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/governance/issues',
        handle: {
          i18nNameKey: 'issues',
          icon: IconZoomExclamation,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/governance/built-in-checks',
        handle: {
          i18nNameKey: 'builtInChecks',
          icon: IconFileStar,
          access: access.user,
          type: 'item',
          activeOnPaths: ['/report/analysis'],
        },
      },
      {
        path: '/governance/custom-policies',
        handle: {
          i18nNameKey: 'customPolicies',
          icon: IconFilePencil,
          access: access.user,
          type: 'item',
          activeOnPaths: ['/policies'],
        },
      },
    ],
  },
  {
    path: '/report/accessibility-statement',
    handle: {
      i18nNameKey: 'accessibilityStatement',
      icon: IconFileDescription,
      access: access.user,
      type: 'item',
      badge: { text: 'demo', showWhen: 'accStatementDemoMode' },
    },
  },
  {
    path: '/report/access',
    handle: {
      name: 'Eye-Able Access',
      icon: IconKey,
      access: access.accessModule,
      type: 'collapse',
    },
    children: [
      {
        path: '/report/access/overview',
        handle: {
          i18nNameKey: 'configurations',
          access: access.user,
          type: 'item',
          icon: IconCheckupList,
        },
      },
      {
        path: '/report/access/installation',
        handle: {
          i18nNameKey: 'installation',
          access: access.user,
          type: 'item',
          icon: IconTools,
        },
      },
      {
        path: '/report/access/dataprivacy',
        handle: {
          i18nNameKey: 'dataPrivacy',
          access: access.user,
          type: 'item',
          icon: IconLockSquare,
        },
      },
      {
        path: '/report/access/settings',
        handle: {
          i18nNameKey: 'settings',
          icon: IconSettings,
          access: access.admin,
          type: 'item',
        },
      },
    ],
  },
  {
    path: '/report/manual-tests',
    handle: {
      i18nNameKey: 'manualTests',
      icon: IconFlask,
      access: access.user,
      type: 'collapse',
      badge: { text: 'new', showWhen: 'always' },
    },
    children: [
      {
        path: '/report/manual-tests',
        handle: {
          i18nNameKey: 'manualTestsPublished',
          access: access.user,
          type: 'item',
          icon: IconFileDescription,
          inactiveOnPaths: [
            '/report/manual-tests/start',
            '/report/manual-tests/scope',
            '/report/manual-tests/test',
          ],
          activeOnQueryIncludes: [
            'published=true',
            'published=1',
            'published=yes',
          ],
        },
      },
      {
        path: '/report/manual-tests/unpublished',
        handle: {
          i18nNameKey: 'manualTestsUnpublished',
          access: access.user,
          type: 'item',
          icon: IconClipboardPlus,
          activeOnPaths: [
            '/report/manual-tests/start',
            '/report/manual-tests/scope',
            '/report/manual-tests/test',
          ],
          inactiveOnQueryIncludes: [
            'published=true',
            'published=1',
            'published=yes',
          ],
        },
      },
    ],
  },
  {
    path: '/ai',
    handle: {
      i18nNameKey: 'eaAi',
      icon: AiNavIcon,
      access: access.user,
      type: 'collapse',
      showDividerBeforeThis: true,
      i18nDividerKey: 'aiSoftware',
      badge: { text: 'new', showWhen: 'always' },
    },
    children: [
      {
        path: '/ai/overview',
        handle: {
          i18nNameKey: 'overview',
          icon: AiNavIcon,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/ai/tests',
        handle: {
          i18nNameKey: 'aiTests',
          icon: IconChecklist,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/ai/ally',
        handle: {
          i18nNameKey: 'allyChat',
          icon: AllyIcon,
          access: access.user,
          type: 'item',
        },
      },
      {
        path: '/ai/transcription',
        handle: {
          i18nNameKey: 'transcription',
          icon: TranscriptionIcon,
          access: access.user,
          type: 'item',
        },
      },
    ],
  },
  {
    path: '/contrast-check',
    handle: {
      i18nNameKey: 'contrastChecker',
      icon: IconPalette,
      access: access.user,
      type: 'item',
      showDividerBeforeThis: true,
      i18nDividerKey: 'tools',
    },
  },
  {
    path: '/admin/panel',
    handle: {
      i18nNameKey: 'usersLicences',
      icon: IconUserShield,
      access: access.salesAdmin,
      type: 'item',
      showDividerBeforeThis: true,
      i18nDividerKey: 'admin',
    },
  },
  {
    path: '/admin/crawler-status',
    handle: {
      i18nNameKey: 'crawlerStatus',
      icon: IconReportAnalytics,
      access: access.admin,
      type: 'item',
    },
  },
];

export default navigation;
